import { lazy, Suspense, useMemo, useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { Route, Link, Routes, Navigate } from "react-router-dom";
import "./App.css";
import LoadingIndicator from "./components/LoadingIndicator";
import SearchBar from "./components/SearchBar";

const Home = lazy(() => import("./pages/SignIn"));
const ForgetPassword = lazy(() => import("./pages/ForgetPassword"));
const PolicyViewer = lazy(() => import("./components/PolicyViewer"));
const BuyerSignUp = lazy(() => import("./pages/BuyerSignUp"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const ContractDetails = lazy(() => import("./pages/ContractDetails"));
const DataPackages = lazy(() => import("./pages/DataPackages"));
const DataTracker = lazy(() => import("./pages/DataTracker"));
const Disputes = lazy(() => import("./pages/Disputes"));
const DownloadData = lazy(() => import("./pages/DownloadData"));
const OrderCreation = lazy(() => import("./pages/OrderCreation"));
const OrderData = lazy(() => import("./pages/OrderData"));
const OrderEdit = lazy(() => import("./pages/OrderEdit"));
const OrdersById = lazy(() => import("./pages/OrdersById"));
const Profile = lazy(() => import("./pages/Profile"));
const TransactionDetail = lazy(() => import("./pages/TransactionDetails"));
const Transactions = lazy(() => import("./pages/Transactions"));
const UserProfile = lazy(() => import("./pages/UserProfile"));
const KnowledgeBase = lazy(() => import("./pages/KnowledgeBase"));
const ContactUs = lazy(() => import("./pages/ContactUs"));

const App = () => {
  const token = localStorage.getItem("auth");
  const { isAuthenticated, loading, theme } = useSelector((state) => state);

  const routes = useMemo(() => {
    const commonRoutes = [
      {
        path: "privacy-policy",
        component: PolicyViewer,
        props: { policyName: "privacy-policy" },
      },
      {
        path: "deletion-policy",
        component: PolicyViewer,
        props: { policyName: "data-deletion-policy" },
      },
      {
        path: "acceptable-use-policy",
        component: PolicyViewer,
        props: { policyName: "acceptable-use-policy" },
      },
      {
        path: "partnerships",
        component: PolicyViewer,
        props: { policyName: "partnerships-policy" },
      },
      {
        path: "terms-conditions",
        component: PolicyViewer,
        props: { policyName: "terms-and-conditions" },
      },
      { path: "contact-us", component: ContactUs },
    ];

    // Public Routes (before authentication)
    const publicRoutes = [
      { path: "/", component: Home },
      { path: "forgetPassword", component: ForgetPassword },
      { path: "buyerSignUp", component: BuyerSignUp },
      ...commonRoutes,
    ];

    // Authenticated Routes (after authentication)
    const authRoutes = [
      { path: "dashboard", component: Dashboard },
      { path: "profile", component: Profile },
      { path: "data-packages", component: DataPackages },
      { path: "transactions", component: Transactions },
      { path: "orderData", component: OrderData },
      { path: "orderCreation", component: OrderCreation },
      { path: "order/edit/:orderId", component: OrderEdit },
      { path: "contract/:orderId", component: ContractDetails },
      { path: "temp-changed", component: DownloadData },
      { path: "orders/:OrdersById", component: OrdersById },
      { path: "transactions/:transactionId", component: TransactionDetail },
      { path: "orders", component: Transactions },
      { path: "disputes", component: Disputes },
      { path: "dataPackages", component: DataPackages },
      { path: "globalContracts", component: DataTracker },
      { path: "userProfile", component: UserProfile },
      { path: "knowledgeBase", component: KnowledgeBase },
      ...commonRoutes,
    ];

    return isAuthenticated && token !== null ? authRoutes : publicRoutes;
  }, [isAuthenticated, token]);

  return (
    <>
      <div className={`${theme === "dark" && "dark "}`}>
        <div
          id="logoutModal-root"
          className={`${theme === "dark" && "dark "}`}
        />

        <Suspense fallback={<LoadingIndicator />}>
          <Routes>
            {routes.map((item) => (
              <Route
                key={item.path}
                path={item.path}
                element={<item.component {...item.props} />}
              />
            ))}

            <Route
              path="*"
              element={<Navigate to={isAuthenticated ? "dashboard" : ""} />}
            />
          </Routes>
        </Suspense>

        {loading && <LoadingIndicator />}
        <div className="z-20 fixed bottom-0 w-full bg-white dark:bg-dark-bg border-t border-gray-200 dark:border-gray-700">
          <ul className="p-0 m-0 flex flex-wrap justify-center items-center lg:justify-end lg:px-6 md:px-4 px-2 max-[375px]:text-[0.5rem] text-[.65rem] sm:text-sm text-slate-500 dark:text-slate-300">
            <li className="px-2 py-2 sm:px-3 sm:py-3 md:px-4">
              <Link
                to="deletion-policy"
                className="hover:text-slate-700 dark:hover:text-slate-100 transition-colors duration-200"
              >
                Deletion Policy
              </Link>
            </li>
            <li className="px-2 py-2 sm:px-3 sm:py-3 md:px-4">
              <Link
                to="terms-conditions"
                className="hover:text-slate-700 dark:hover:text-slate-100 transition-colors duration-200"
              >
                Terms & Conditions
              </Link>
            </li>
            <li className="px-2 py-2 sm:px-3 sm:py-3 md:px-4">
              <Link
                to="privacy-policy"
                className="hover:text-slate-700 dark:hover:text-slate-100 transition-colors duration-200"
              >
                Privacy Policy
              </Link>
            </li>
            <li className="px-2 py-2 sm:px-3 sm:py-3 md:px-4">
              <Link
                to="acceptable-use-policy"
                className="hover:text-slate-700 dark:hover:text-slate-100 transition-colors duration-200"
              >
                Acceptable Use
              </Link>
            </li>
            <li className="px-2 py-2 sm:px-3 sm:py-3 md:px-4">
              <Link
                to="contact-us"
                className="hover:text-slate-700 dark:hover:text-slate-100 transition-colors duration-200"
              >
                Contact Us
              </Link>
            </li>
          </ul>
        </div>
      </div>
    </>
  );
};

export default App;
